'use client';

import { useState, useCallback, useRef, useLayoutEffect, type FC, type CSSProperties } from 'react';
import { cn } from '../../utils/cn';

interface BlurImageProps {
  src: string;
  placeholder?: string | null;
  alt: string;
  className?: string;
  style?: CSSProperties;
}

const isImageCached = (src: string): boolean => {
  if (typeof window === 'undefined') return false;
  const img = new window.Image();
  img.src = src;
  return img.complete && img.naturalWidth > 0;
};

export const BlurImage: FC<BlurImageProps> = ({
  src,
  placeholder,
  alt,
  className,
  style,
}) => {
  const [isLoaded, setIsLoaded] = useState(() => isImageCached(src));
  const [hasFailed, setHasFailed] = useState(false);
  const prevSrcRef = useRef(src);

  useLayoutEffect(() => {
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      setIsLoaded(isImageCached(src));
      setHasFailed(false);
    }
  }, [src]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleError = useCallback(() => {
    setHasFailed(true);
  }, []);

  const showPlaceholder = !!placeholder && (!isLoaded || hasFailed);

  return (
    <div className={cn('overflow-hidden', className)} style={style}>
      <img
        src={showPlaceholder ? placeholder : src}
        alt={alt}
        className={cn('block w-full h-full object-cover', { 'blur-[30px] scale-110': showPlaceholder })}
        onLoad={!showPlaceholder ? handleLoad : undefined}
      />

      {showPlaceholder && !hasFailed && (
        <img
          src={src}
          alt=""
          className="absolute w-px h-px opacity-0 pointer-events-none"
          onLoad={handleLoad}
          onError={handleError}
          aria-hidden="true"
        />
      )}
    </div>
  );
};
